import {
  CHART_NOTES_BILLING_TYPE,
  CHART_NOTES_GENERAL_TYPE,
  CHART_NOTES_PHONE_TYPE,
  CHART_NOTES_VISIT_TYPE
} from "constants/chart-notes";
import {
  GROUP_AUDIOLOGISTS,
  GROUP_BUSINESS_OWNERS,
  GROUP_SUNO
} from "constants/staff";

// ENG-2484. The groups that see visit notes while HIDE_VISIT_NOTES_BY_ROLE_FEATURE is on. Matched by
// exact group name, so a practice's own group (e.g. "Chicagoland Audiologists") does not count.
export const VISIT_NOTE_VIEWER_GROUPS: readonly string[] = [
  GROUP_AUDIOLOGISTS,
  GROUP_BUSINESS_OWNERS,
  GROUP_SUNO
];

// Every chart note type except Patient Visit, as the list endpoint's comma-separated `type` filter.
export const NON_VISIT_CHART_NOTE_TYPES = [
  CHART_NOTES_GENERAL_TYPE,
  CHART_NOTES_BILLING_TYPE,
  CHART_NOTES_PHONE_TYPE
].join(",");

export function canViewVisitNotes(
  isFeatureOn: boolean,
  groupNames: readonly string[]
): boolean {
  if (!isFeatureOn) return true;
  return groupNames.some((name) => VISIT_NOTE_VIEWER_GROUPS.includes(name));
}

export function isVisitNoteHidden(
  note: { type?: number | null } | null | undefined,
  canView: boolean
): boolean {
  return !canView && note?.type === CHART_NOTES_VISIT_TYPE;
}

// The `type` a chart-notes list request should send. A user who can see visit notes keeps the one
// asked for. Anyone else never asks for visit notes: "all types" (undefined or null) and "visit only"
// both become every other type.
export function chartNoteTypeFilterFor<
  T extends number | string | null | undefined
>(type: T, canView: boolean): T | string {
  if (canView) return type;
  if (type === undefined || type === null || type === CHART_NOTES_VISIT_TYPE) {
    return NON_VISIT_CHART_NOTE_TYPES;
  }
  return type;
}

// The chart-notes tab to show. Someone who may not see visit notes is never on the Visit Notes tab:
// a remembered or default "visit" tab shows them All Notes.
export function effectiveChartNoteTab(
  storedTab: string,
  canView: boolean
): string {
  return !canView && storedTab === "visit" ? "all" : storedTab;
}

// Extra list filters for a request that must not return visit notes to this user: none for someone
// who may see them, so their request is unchanged.
export function visitNoteTypeFilter(canView: boolean): { type?: string } {
  return canView ? {} : { type: NON_VISIT_CHART_NOTE_TYPES };
}
